import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import { unwrap, messageOf } from '@truecare/api-client';
import type { CaregiverStatus } from '@truecare/schemas';
import { api } from '../api';

/**
 * Caregiver "Payment & Tax" tab — the pilot-critical self-service that was
 * missing: banking (with void cheque), SIN, and HST. Backend gates all writes
 * on caregiver.status === ACTIVE, so the whole surface is read-only guidance
 * until activation.
 *
 * Two shapes once ACTIVE:
 *   - SIN not on file → one "Complete your tax info" bulk submit
 *     (POST /me/caregiver/tax-info: sin + banking + void cheque + hst).
 *   - SIN on file → SIN read-only + banking update (PATCH /me/caregiver/banking)
 *     + HST toggle (PATCH /me/caregiver/hst).
 */

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const BANKING_KEY = ['caregiver', 'banking'];
const PROFILE_KEY = ['caregiver', 'me'];
// Phase 5g audit (medium) — submitting banking/SIN/HST during ONBOARDING moves
// the ONBOARDING → ACTIVE gate, so the Get-started checklist (which reads this
// key) must refetch or it shows stale "todo" rows after a successful submit.
const ONBOARDING_READINESS_KEY = ['caregiver', 'onboarding-readiness'];

async function uploadVoidCheque(file: File): Promise<string> {
  const urlResp = (await unwrap(
    api.POST('/api/v1/me/caregiver/banking/upload-url', {
      body: { contentType: file.type, contentLength: file.size },
    }),
  )) as { uploadUrl: string; objectStorageKey: string };
  const put = await fetch(urlResp.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!put.ok) throw new Error('Void cheque upload failed — please try again.');
  return urlResp.objectStorageKey;
}

export function CaregiverPaymentTaxSection({
  status,
  hstRegistered,
}: {
  status: CaregiverStatus;
  hstRegistered: boolean;
}) {
  // Phase 5g — payment/tax is now collected during ONBOARDING (to close the
  // gate to ACTIVE) and stays editable while ACTIVE. Pre-approval states never
  // render this section (parent gates on showPaymentTax); the guard stays
  // defensive for SUSPENDED/TERMINATED.
  if (status !== 'ACTIVE' && status !== 'ONBOARDING') {
    return (
      <Card padding="lg" accent="left" tone="gold">
        <CardHeader title="Payment & tax" description="How you get paid." />
        <Alert tone="info" className="mt-4">
          Payment and tax setup opens once TrueCare activates your account.
          You'll add your banking (with a void cheque), tax identity, and HST
          status here — everything needed to run your payouts.
        </Alert>
      </Card>
    );
  }

  // Each concern is its own independently-submittable card (banking + tax
  // identity are separate ONBOARDING gate items; HST is optional).
  return (
    <div className="flex flex-col gap-6">
      <TaxIdentityCard />
      <BankingCard />
      <HstCard hstRegistered={hstRegistered} />
    </div>
  );
}

// ── Tax identity — SIN (individual) or Business Number (corporation) ──────────

const TAX_IDENTITY_KEY = ['caregiver', 'tax-identity'];

function TaxIdentityCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: TAX_IDENTITY_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/tax-identity')),
  });
  const data = q.data as
    | {
        taxEntityType: 'INDIVIDUAL' | 'CORPORATION';
        sinOnFile: boolean;
        businessNumberOnFile: boolean;
        businessNumberLast3: string | null;
      }
    | undefined;
  const onFile = !!data && (data.sinOnFile || data.businessNumberOnFile);

  const [entityType, setEntityType] = useState<'INDIVIDUAL' | 'CORPORATION'>('INDIVIDUAL');
  const [sin, setSin] = useState('');
  const [bn, setBn] = useState('');
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const body =
        entityType === 'CORPORATION'
          ? { taxEntityType: 'CORPORATION', businessNumber: bn }
          : { taxEntityType: 'INDIVIDUAL', sin };
      await unwrap(
        api.PATCH('/api/v1/me/caregiver/tax-identity' as never, { body } as never),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TAX_IDENTITY_KEY });
      void qc.invalidateQueries({ queryKey: ONBOARDING_READINESS_KEY });
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
      setEditing(false);
      setSin('');
      setBn('');
    },
  });

  const canSubmit =
    entityType === 'CORPORATION'
      ? /^\d{9}(\s?[A-Za-z]{2}\s?\d{4})?$/.test(bn.trim())
      : /^\d{9}$|^\d{3}[- ]\d{3}[- ]\d{3}$/.test(sin);

  const showForm = !onFile || editing;

  return (
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title="Tax identity"
        description="Used for your year-end T4A. Provide your SIN if you work as an individual (sole proprietor), or your Business Number if you're incorporated."
      />
      <div className="mt-4 flex flex-col gap-4">
        {onFile && !editing && (
          <Alert tone="success" data-testid="caregiver-tax-identity-onfile">
            {data.taxEntityType === 'CORPORATION'
              ? `Your Business Number is on file${data.businessNumberLast3 ? ` (•••${data.businessNumberLast3})` : ''}.`
              : 'Your SIN is on file.'}{' '}
            Contact TrueCare if it needs to change.
          </Alert>
        )}

        {showForm && (
          <>
            <fieldset
              className="flex flex-col gap-2"
              data-testid="caregiver-tax-entity-type"
            >
              <label className="flex items-center gap-2 font-body text-sm">
                <input
                  type="radio"
                  name="taxEntityType"
                  checked={entityType === 'INDIVIDUAL'}
                  onChange={() => setEntityType('INDIVIDUAL')}
                  data-testid="tax-entity-individual"
                />
                Individual / sole proprietor — I&apos;ll provide my SIN
              </label>
              <label className="flex items-center gap-2 font-body text-sm">
                <input
                  type="radio"
                  name="taxEntityType"
                  checked={entityType === 'CORPORATION'}
                  onChange={() => setEntityType('CORPORATION')}
                  data-testid="tax-entity-corporation"
                />
                Incorporated / a business — I&apos;ll provide my Business Number
              </label>
            </fieldset>

            {entityType === 'INDIVIDUAL' ? (
              <Field label="Social Insurance Number (SIN)" hint="9 digits">
                <Input
                  inputMode="numeric"
                  autoComplete="off"
                  value={sin}
                  onChange={(e) => setSin(e.target.value)}
                  placeholder="123-456-789"
                  data-testid="caregiver-sin-input"
                />
              </Field>
            ) : (
              <Field label="Business Number (BN)" hint="9 digits">
                <Input
                  inputMode="numeric"
                  value={bn}
                  onChange={(e) => setBn(e.target.value)}
                  placeholder="123456789"
                  data-testid="caregiver-bn-input"
                />
              </Field>
            )}

            {mutation.isError && (
              <Alert tone="danger">{messageOf(mutation.error)}</Alert>
            )}
            <div className="flex justify-end gap-3">
              {onFile && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                variant="primary"
                disabled={!canSubmit}
                loading={mutation.isPending}
                onClick={() => mutation.mutate()}
                data-testid="caregiver-tax-identity-submit"
              >
                Save tax identity
              </Button>
            </div>
          </>
        )}

        {onFile && !editing && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setEditing(true);
                setEntityType(data.taxEntityType);
              }}
              data-testid="caregiver-tax-identity-edit"
            >
              Change
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function BankingCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const bankingQuery = useQuery({
    queryKey: BANKING_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/caregiver/banking')),
  });

  const [transit, setTransit] = useState('');
  const [institution, setInstitution] = useState('');
  const [account, setAccount] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Attach a void cheque (image or PDF).');
      const voidChequeObjectKey = await uploadVoidCheque(file);
      await unwrap(
        api.PATCH('/api/v1/me/caregiver/banking' as never, {
          body: {
            banking: { transit, institution, account },
            voidChequeObjectKey,
          },
        } as never),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ONBOARDING_READINESS_KEY });
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
      setEditing(false);
      setTransit('');
      setInstitution('');
      setAccount('');
      setFile(null);
      void qc.invalidateQueries({ queryKey: BANKING_KEY });
    },
  });

  const b = bankingQuery.data as
    | {
        eftSubmitted: boolean;
        eftVerified: boolean;
        eftRejected: boolean;
        eftRejectionReason: string | null;
        accountLast4: string | null;
        transitLast3: string | null;
        institution: string | null;
        payoutFrequency: string;
      }
    | undefined;

  const state = !b?.eftSubmitted
    ? 'Not submitted'
    : b.eftVerified
      ? 'Verified'
      : 'Submitted — awaiting verification';

  const canSubmit =
    /^\d{5}$/.test(transit) &&
    /^\d{3}$/.test(institution) &&
    /^\d{7,12}$/.test(account) &&
    file !== null;

  return (
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title="Banking (EFT)"
        description="Where your payouts are deposited. Updating your banking resets verification — an operator re-checks the new void cheque."
      />
      <div className="mt-4 flex flex-col gap-3">
        {bankingQuery.isLoading ? (
          <p className="font-body text-sm text-ink-muted">Loading…</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-body text-sm">
              <span>
                Status:{' '}
                <strong
                  className={b?.eftVerified ? 'text-success' : 'text-ink'}
                >
                  {state}
                </strong>
              </span>
              {b?.accountLast4 && (
                <span className="text-ink-muted">
                  Account •••{b.accountLast4} · transit •••{b.transitLast3} ·
                  institution {b.institution}
                </span>
              )}
              {b && (
                <span className="text-ink-muted">
                  Payout frequency: {b.payoutFrequency.toLowerCase()}
                </span>
              )}
            </div>
            {b?.eftRejected && (
              <Alert tone="warning" title="Banking needs re-submission">
                {b.eftRejectionReason ??
                  'Your last void cheque could not be verified — please re-submit.'}
              </Alert>
            )}
          </>
        )}

        {editing ? (
          <div className="mt-2 flex flex-col gap-4">
            <BankingFields
              transit={transit}
              institution={institution}
              account={account}
              onTransit={setTransit}
              onInstitution={setInstitution}
              onAccount={setAccount}
              file={file}
              onFile={setFile}
            />
            {mutation.isError && (
              <Alert tone="danger">{messageOf(mutation.error)}</Alert>
            )}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canSubmit}
                loading={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                Submit banking
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(true)}
              data-testid="caregiver-banking-edit"
            >
              {b?.eftSubmitted ? 'Update banking' : 'Add banking'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function HstCard({ hstRegistered }: { hstRegistered: boolean }) {
  const qc = useQueryClient();
  const [registered, setRegistered] = useState(hstRegistered);
  const [hstNumber, setHstNumber] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.PATCH('/api/v1/me/caregiver/hst' as never, {
          body: {
            hstRegistered: registered,
            hstNumber: registered ? hstNumber : undefined,
          },
        } as never),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ONBOARDING_READINESS_KEY });
      void qc.invalidateQueries({ queryKey: PROFILE_KEY });
    },
  });

  // Saveable when the form is valid: "not registered" always, or "registered"
  // with a well-formed HST number (idempotent re-save is harmless).
  const canSubmit = !registered || /^\d{9}RT\d{4}$/i.test(hstNumber);

  return (
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title="HST registration"
        description="Optional — most care professionals aren't registered. If that's you, leave it off; there's nothing to do here and it won't affect your account or activation. Turn it on only once you've registered for HST/GST with the CRA (required after you cross the $30K annual threshold)."
      />
      <div className="mt-4 flex flex-col gap-4">
        <HstFields
          hstRegistered={registered}
          hstNumber={hstNumber}
          onToggle={setRegistered}
          onNumber={setHstNumber}
        />
        {mutation.isError && (
          <Alert tone="danger">{messageOf(mutation.error)}</Alert>
        )}
        {mutation.isSuccess && (
          <Alert tone="success">
            {registered
              ? 'HST registration saved.'
              : "Saved — you're marked as not HST-registered. That's fine and doesn't affect your account or activation."}
          </Alert>
        )}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Save HST status
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ── Shared field groups ──────────────────────────────────────────────────────

function BankingFields({
  transit,
  institution,
  account,
  onTransit,
  onInstitution,
  onAccount,
  file,
  onFile,
}: {
  transit: string;
  institution: string;
  account: string;
  onTransit: (v: string) => void;
  onInstitution: (v: string) => void;
  onAccount: (v: string) => void;
  file: File | null;
  onFile: (f: File | null) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Transit" hint="5 digits">
          <Input
            inputMode="numeric"
            value={transit}
            onChange={(e) => onTransit(e.target.value.replace(/\D/g, '').slice(0, 5))}
          />
        </Field>
        <Field label="Institution" hint="3 digits">
          <Input
            inputMode="numeric"
            value={institution}
            onChange={(e) =>
              onInstitution(e.target.value.replace(/\D/g, '').slice(0, 3))
            }
          />
        </Field>
        <Field label="Account" hint="7–12 digits">
          <Input
            inputMode="numeric"
            value={account}
            onChange={(e) =>
              onAccount(e.target.value.replace(/\D/g, '').slice(0, 12))
            }
          />
        </Field>
      </div>
      <Field label="Void cheque (JPEG, PNG, WebP, or PDF — max 20 MB)">
        <input
          type="file"
          accept={ACCEPT}
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          className="block w-full font-body text-sm text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-forest file:px-4 file:py-2 file:font-body file:text-sm file:font-semibold file:text-paper hover:file:bg-forest-dark"
          data-testid="caregiver-void-cheque"
        />
      </Field>
      {file && (
        <span className="-mt-2 block text-xs text-ink-muted">{file.name}</span>
      )}
    </div>
  );
}

function HstFields({
  hstRegistered,
  hstNumber,
  onToggle,
  onNumber,
}: {
  hstRegistered: boolean;
  hstNumber: string;
  onToggle: (v: boolean) => void;
  onNumber: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-center gap-2 font-body text-sm">
        <input
          type="checkbox"
          checked={hstRegistered}
          onChange={(e) => onToggle(e.target.checked)}
          data-testid="caregiver-hst-toggle"
        />
        I'm registered for HST/GST with the CRA
      </label>
      {hstRegistered && (
        <Field label="HST number" hint="Format: 123456789RT0001">
          <Input
            value={hstNumber}
            onChange={(e) => onNumber(e.target.value.toUpperCase())}
            placeholder="123456789RT0001"
          />
        </Field>
      )}
    </div>
  );
}
